import { userActions } from '@/actions';
import { useAppDispatch } from '@/hooks';
import { createContext, MouseEvent, ReactNode, useContext, useState } from 'react';
import { AccountNavItemIdentifier } from './AccountNavigation.types';

export type AccountNavigationContextType = ReturnType<typeof useAccountNavigationManager>;

const useAccountNavigationManager = () => {
  const [show, setShow] = useState(false);
  const [showConfirmLogoutModal, setShowConfirmLogoutModal] = useState(false);
  const dispatch = useAppDispatch();

  function handleOnClose() {
    setShow(false);
  }
  function handleOnOpen() {
    setShow(true);
  }

  function handleOnAbortLogout() {
    setShowConfirmLogoutModal(false);
  }

  function handleOnLogoutConfirm() {
    handleOnClose();
    dispatch(userActions.logout());
  }

  function handleOnLogoutClick(event: MouseEvent<HTMLButtonElement>) {
    event.currentTarget.blur();
    setShowConfirmLogoutModal(true);
  }

  function handleOnNavigationItemClick(identifier: AccountNavItemIdentifier) {
    switch (identifier) {
      default:
        handleOnClose();
    }
  }

  return {
    show,
    onClose: handleOnClose,
    onOpen: handleOnOpen,
    showConfirmLogoutModal,
    onAbortLogout: handleOnAbortLogout,
    onLogoutClick: handleOnLogoutClick,
    onLogoutConfirm: handleOnLogoutConfirm,
    onNavigationItemClick: handleOnNavigationItemClick,
  };
};

export const AccountNavigationContext = createContext<AccountNavigationContextType>({
  show: false,
  onClose: () => {},
  onOpen: () => {},
  showConfirmLogoutModal: false,
  onAbortLogout: () => {},
  onLogoutClick: () => {},
  onLogoutConfirm: () => {},
  onNavigationItemClick: () => {},
});

export const useAccountNavigationContext = () => {
  const context = useContext(AccountNavigationContext);

  if (!context) {
    throw new Error('useAccountNavigationContext must be used within a AccountNavigationProvider');
  }

  return context;
};

export const AccountNavigationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  return (
    <AccountNavigationContext.Provider value={useAccountNavigationManager()}>
      {children}
    </AccountNavigationContext.Provider>
  );
};
